import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Alert, Typography, Popover, Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useHistory, useParams } from 'react-router-dom';
import Link from 'antd/lib/typography/Link';
import { DKIM_STATUS } from '../../../../interfaces/api/Senders';
import { EmailerSendersGETResponse } from '../../../../interfaces/backend/EmailerSendersGETResponse';
import { TemplatesPOSTRequest } from '../../../../interfaces/backend/TemplatesPOSTRequest';
import { Template } from '../../../../interfaces/backend/Template';
import styled from 'styled-components';
import TemplateCardContextMenu from '../TemplateCardContextMenu';
import { liquidizeTemplate } from '../../LiquidizedPreview';
import { blue } from '@ant-design/colors';
import { WarningFilled } from '@ant-design/icons';
import EmailPreviewIframe from './EmailPreviewIframe';
import { TemplatesPATCHDefaultRequest } from '../../../../interfaces/backend/TemplatesPATCHDefaultRequest';
import {
  DeliveryOptions,
  Notification
} from '../../../../interfaces/backend/Notifications';
import { EmailDeliveryOptionsEnum } from '../../NotificationTabsContainer';

type EmailTemplate = Extract<Template, { channel: 'EMAIL' }>;

const CustomCard = styled(Card)`
  cursor: pointer;
  &:hover {
    border: 1px solid ${blue.primary};
    transition: border 0.3s ease;
  }

  & .ant-card-cover {
    margin-top: 1px;
    margin-left: 0px;
    margin-right: 0px;
    position: relative;
    padding: 20px;
  }

  &:hover .ant-card-cover > .ant-btn {
    opacity: 1;
    transition: opacity 0.3s;
  }

  & .ant-card-cover > .ant-btn {
    opacity: 0;
    position: absolute;
    zindex: 1;
    bottom: -1px;
    left: 1px;
    right: 1px;
    width: auto;
  }
`;

interface Props {
  template: Extract<Template, { channel: 'EMAIL' }>;
  notification: Notification;
  sender?: EmailerSendersGETResponse[number];
  deleteTemplate: (channel: string, templateId: string) => Promise<void>;
  makeDefault: (
    channel: string,
    templateId: string,
    modes: TemplatesPATCHDefaultRequest['modes']
  ) => Promise<void>;
  patchTemplate: (
    channel: string,
    templateId: string,
    params: Record<string, unknown>
  ) => Promise<void>;
  createTemplate: (
    channel: string,
    params: TemplatesPOSTRequest
  ) => Promise<void>;
  setTemplateBeingDuplicated: (template: Template) => void;
}

const EmailTemplateCard = (props: Props): JSX.Element => {
  const { accountId } = useParams<{ accountId: string }>();
  const [liquidizedTemplate, setLiquidizedTemplate] = useState<EmailTemplate>();
  const [previewError, setPreviewError] = useState<React.ReactNode>();
  const [previewWarning, setPreviewWarning] = useState<React.ReactNode>();
  const history = useHistory();

  const defaultDeliveryList: string[] = Object.entries(
    props.template.isDefaultFor
  )
    .filter(([, isDefault]) => isDefault)
    .map(([option]) => option)
    .filter((option) => {
      const emailOptions = props.notification.options?.EMAIL as DeliveryOptions;
      return emailOptions[option as EmailDeliveryOptionsEnum]?.enabled;
    });

  useEffect(() => {
    const { result, warning, error } = liquidizeTemplate(props.template);
    setLiquidizedTemplate(result as EmailTemplate);
    setPreviewWarning(warning);
    setPreviewError(error);
  }, [props.template]);

  const generateActionButtons = (): JSX.Element[] => {
    const buttons = [
      <TemplateCardContextMenu
        template={props.template}
        notification={props.notification}
        setTemplateBeingDuplicated={props.setTemplateBeingDuplicated}
        makeDefault={props.makeDefault}
        deleteTemplate={props.deleteTemplate}
      />
    ];

    return buttons;
  };

  const getEmailSenderAlert = (): React.ReactNode => {
    if (!props.sender)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              This email will be sent from our default address, because your
              FROM address is not verified on our end. Go to{' '}
              <Link href={`/${accountId}/settings/domain-verification`}>
                {`Settings->Domain Verification`}
              </Link>{' '}
              to verify your domain.
            </Typography.Paragraph>
          }
          type="error"
          style={{ marginBottom: '20px' }}
        />
      );

    if (props.sender?.status === DKIM_STATUS.FAILED)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              The verification process for your FROM address has failed. Please
              go to{' '}
              <Typography.Link
                href={`/${accountId}/settings/domain-verification`}
              >
                Settings/Domain Verification
              </Typography.Link>{' '}
              to retry. Until verification is successful, emails will continue
              to go out from noreply@notificationapi.com.
            </Typography.Paragraph>
          }
          type="error"
          style={{ marginBottom: '20px' }}
        />
      );

    if (props.sender?.status === DKIM_STATUS.PENDING)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              Follow the instructions in{' '}
              <Link href={`/${accountId}/settings/domain-verification`}>
                Setting/Domain Verification
              </Link>{' '}
              to verify your address. Until verification is successful, emails
              will continue going out from noreply@notificationapi.com.
            </Typography.Paragraph>
          }
          type="warning"
          style={{ marginBottom: '20px' }}
        />
      );
  };

  return (
    <Col span={24} lg={12} xl={8}>
      <CustomCard
        title={
          <Row>
            Email
            <>
              {getEmailSenderAlert() !== undefined && (
                <Popover
                  overlayInnerStyle={{
                    padding: 0
                  }}
                  content={
                    <div style={{ maxWidth: '400px' }}>
                      {getEmailSenderAlert()}
                    </div>
                  }
                >
                  <Text type="warning" style={{ marginLeft: '5px' }}>
                    <WarningFilled />
                  </Text>
                </Popover>
              )}
            </>
            <div style={{ marginLeft: 8 }}>
              {defaultDeliveryList?.map((delivery) => {
                return (
                  <Tag color="green" key={delivery}>
                    {delivery.charAt(0).toUpperCase() + delivery.slice(1)}
                  </Tag>
                );
              })}
            </div>
          </Row>
        }
        onClick={() => {
          history.push(
            `/${accountId}/notifications/${props.template.notificationId}/beeEditor/${props.template.templateId}`
          );
        }}
        actions={generateActionButtons()}
      >
        <Row style={{ marginBottom: '10px' }}>
          <Text>Subject: {liquidizedTemplate?.subject}</Text>
        </Row>
        <Row style={{ marginBottom: '15px' }}>
          <Col>
            <Text type="secondary">
              From: {props.template.senderName}{' '}
              {'<' + props.template.senderEmail + '>'}
            </Text>
          </Col>
        </Row>
        <EmailPreviewIframe
          src={liquidizedTemplate?.html}
          scale={0.6}
          clickable={false}
        />
      </CustomCard>

      {previewWarning}
      {previewError}
    </Col>
  );
};

export default EmailTemplateCard;
